/**
 * Canonical URLs for the apex self-serve signup flow (M9).
 *
 * Clerk OAuth must redirect to an absolute URL on the console Worker
 * (`solvait.ai/signup*`), not a relative path. Relative paths and the
 * deprecated `afterSignUpUrl` default to `/`, which on apex is the
 * marketing site → `/en`, leaving new users signed in but off the
 * company/domain step.
 *
 * When the user arrives from the marketing site (/ar or /en), links
 * carry `?lng=ar|en`. These helpers preserve that param on Clerk
 * redirects and in-app navigation so the signup UI stays translated.
 */

import type { Locale } from "../i18n";
import { readLocaleFromWindow } from "./locale-from-url";

export { localeFromSearch } from "./locale-from-url";

const PRODUCTION_ORIGIN = "https://solvait.ai";

export function signupOrigin(): string {
  if (typeof window !== "undefined" && window.location.origin) {
    return window.location.origin;
  }
  return PRODUCTION_ORIGIN;
}

function activeLocaleFromWindow(): Locale | null {
  return readLocaleFromWindow();
}

/** Suffix for React Router paths, e.g. `?lng=ar`. */
export function signupLocaleSearch(locale?: Locale | null): string {
  const loc = locale ?? activeLocaleFromWindow();
  return loc ? `?lng=${loc}` : "";
}

function withLng(path: string, locale?: Locale | null): string {
  const loc = locale ?? activeLocaleFromWindow();
  const base = `${signupOrigin()}${path}`;
  return loc ? `${base}?lng=${loc}` : base;
}

export function signupCompanyStepUrl(locale?: Locale | null): string {
  return withLng("/signup/company", locale);
}

export function signupAuthStepUrl(locale?: Locale | null): string {
  return withLng("/signup", locale);
}

/** Apex sign-in entry — separate Clerk `path` so sub-steps stay on /signin/*. */
export function signupSigninStepUrl(locale?: Locale | null): string {
  return withLng("/signin", locale);
}
